// PATH: src/pages/EstudioBiblico.jsx
// VERSION: 3.0 — Fetch a /api/studies, sin JSON local; normaliza payload; UI igual
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import {
  Box,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Button,
  useDisclosure,
  Collapse,
  Container,
  useBreakpointValue,
  Input,
  Flex,
  AspectRatio,
  HStack,
  Link,
  Spinner,
  useColorModeValue,
  LightMode,
  Badge,
} from '@chakra-ui/react';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { FiImage, FiFileText, FiHeart } from 'react-icons/fi';
import { FaHeart } from 'react-icons/fa';
import html2pdf from 'html2pdf.js';
import { toJpeg } from 'html-to-image';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react';
import { API } from '../utils/apiBase';
import { useFavorites } from '../utils/useFavorites';
import { useProgress } from '../utils/useProgress';
import AudioPlayerButton from '../components/common/AudioPlayerButton';
import { IconButton, Switch, FormControl, FormLabel } from '@chakra-ui/react';

const PAGE_SIZE = 9;

// --- Helpers: fetch + normalizar ---
async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
// Acepta {items}, {studies}, {data} o array directo
function normalizeStudiesPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.studies)) return payload.studies;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

// --- Helpers: sanitizar y convertir URLs de YouTube a embed/watch ---
function getYouTubeUrls(raw) {
  if (!raw) return { watchUrl: null, embedUrl: null };
  let s = String(raw).trim();

  // Si viene en Markdown [texto](url)
  const md = s.match(/\((https?:\/\/[^\s)]+)\)/);
  if (md) s = md[1];

  // Si viene "[https://...]" literal
  if (/^\[.*\]$/.test(s)) s = s.slice(1, -1);

  try {
    const u = new URL(s);
    const host = u.hostname.replace(/^www\./, '');
    let videoId = null;
    const listId = u.searchParams.get('list');

    if (host === 'youtu.be') {
      videoId = u.pathname.split('/').filter(Boolean)[0] || null;
    } else if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (u.pathname.startsWith('/watch')) videoId = u.searchParams.get('v');
      else if (u.pathname.startsWith('/shorts/')) videoId = u.pathname.split('/')[2] || null;
      else if (u.pathname.startsWith('/live/')) videoId = u.pathname.split('/')[2] || null;
    }

    const watchUrl = videoId
      ? `https://www.youtube.com/watch?v=${videoId}${listId ? `&list=${listId}` : ''}`
      : listId
        ? `https://www.youtube.com/playlist?list=${listId}`
        : s;

    const embedUrl =
      listId && !videoId
        ? `https://www.youtube.com/embed/videoseries?list=${listId}`
        : videoId
          ? `https://www.youtube.com/embed/${videoId}${listId ? `?list=${listId}` : ''}`
          : null;

    return { watchUrl, embedUrl };
  } catch {
    return { watchUrl: s, embedUrl: null };
  }
}

// Componente para formatear y estructurar la Reflexión del Estudio Bíblico en tarjetas/cajas estéticas
function ReflexionViewer({ text, isCapture = false }) {
  const cardBg = useColorModeValue('bg.card', 'gray.800');
  const subtleBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const borderColor = useColorModeValue('border.default', 'gray.700');
  const prayerBg = useColorModeValue('purple.50', 'rgba(159, 122, 234, 0.08)');
  const prayerBorder = useColorModeValue('purple.200', 'purple.800');
  const readingBg = useColorModeValue('blue.50', 'rgba(66, 153, 225, 0.08)');
  const readingBorder = useColorModeValue('blue.200', 'blue.800');

  const sections = useMemo(() => {
    if (!text) return [];
    const normalized = text.trim();
    const rawParts = normalized.split(/(?:^|\n\s*\n|\n)(?=\d+[.)]\s*[^\n]+)/);
    const result = [];

    for (const part of rawParts) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const match = trimmed.match(/^(\d+[.)]\s*[^\n]+)\n*([\s\S]*)$/);
      if (match) {
        let rawHeader = match[1].trim();
        let content = match[2].trim();

        let numMatch = rawHeader.match(/^(\d+)[.)]\s*(.*)$/);
        let num = numMatch ? numMatch[1] : '';
        let title = numMatch ? numMatch[2] : rawHeader;
        title = title.replace(/:+$/, '').trim();

        // Capitalizar título en formato limpio
        title = title.toLowerCase().replace(/(^|\s|\/)([a-záéíóúñü])/g, (m, p1, p2) => p1 + p2.toUpperCase());
        const lowerWords = ['De', 'Y', 'En', 'Los', 'Las', 'El', 'La', 'Del', 'Al', 'Por', 'Para', 'Con', 'Un', 'Una', 'O', 'E', 'U'];
        lowerWords.forEach(w => {
          const reg = new RegExp('(\\s)' + w + '(\\s)', 'g');
          title = title.replace(reg, (m, p1, p2) => p1 + w.toLowerCase() + p2);
        });
        title = title.charAt(0).toUpperCase() + title.slice(1);

        if (title.toLowerCase().includes('oración') || title.toLowerCase().includes('oracion')) {
          continue; // Skip the prayer section for studies
        }

        result.push({ isNumbered: true, num, title, content });
      } else {
        result.push({ isNumbered: false, content: trimmed });
      }
    }
    return result;
  }, [text]);

  if (!text) return null;

  const getSectionStyle = (title = '') => {
    const t = title.toLowerCase();
    if (t.includes('oración') || t.includes('oracion')) {
      return {
        icon: '🙏',
        badgeColor: 'purple',
        bg: isCapture ? '#FAF5FF' : prayerBg,
        borderColor: isCapture ? '#E9D8FD' : prayerBorder,
        borderLeft: '4px solid #9F7AEA',
        isPrayer: true
      };
    }
    if (t.includes('lectura') || t.includes('adicional')) {
      return {
        icon: '📚',
        badgeColor: 'teal',
        bg: isCapture ? '#F0FDF4' : readingBg,
        borderColor: isCapture ? '#C6F6D5' : readingBorder,
        borderLeft: '4px solid #38B2AC',
        isReading: true
      };
    }
    if (t.includes('aplicación') || t.includes('aplicacion')) {
      return {
        icon: '💡',
        badgeColor: 'orange',
        bg: isCapture ? '#FFFFFF' : cardBg,
        borderColor: isCapture ? '#E2E8F0' : borderColor,
        borderLeft: '4px solid #ED8936'
      };
    }
    if (t.includes('análisis') || t.includes('analisis') || t.includes('versículos') || t.includes('versiculos')) {
      return {
        icon: '🔍',
        badgeColor: 'blue',
        bg: isCapture ? '#FFFFFF' : cardBg,
        borderColor: isCapture ? '#E2E8F0' : borderColor,
        borderLeft: '4px solid #3182CE'
      };
    }
    // Default (Contexto Histórico y Cultural)
    return {
      icon: '📜',
      badgeColor: 'cyan',
      bg: isCapture ? '#FFFFFF' : cardBg,
      borderColor: isCapture ? '#E2E8F0' : borderColor,
      borderLeft: '4px solid #00B5D8'
    };
  };

  const hasNumbered = sections.some(s => s.isNumbered);
  if (!hasNumbered) {
    return (
      <Box w="100%">
        <Text fontWeight="bold" mb={3} color={isCapture ? "gray.800" : "text.default"}>
          Reflexión y Análisis
        </Text>
        <Box
          bg={isCapture ? "#F7FAFC" : subtleBg}
          border="1px solid"
          borderColor={isCapture ? "#E2E8F0" : borderColor}
          borderRadius="lg"
          p={4}
        >
          <VStack align="start" spacing={3}>
            {text.split(/\n\s*\n/).map((paragraph, pIdx) => (
              <Text 
                key={pIdx} 
                color={isCapture ? "gray.700" : "text.default"}
                lineHeight="tall"
              >
                {paragraph.trim()}
              </Text>
            ))}
          </VStack>
        </Box>
      </Box>
    );
  }

  return (
    <Box w="100%">
      <Text fontWeight="bold" fontSize="md" mb={3} color={isCapture ? "gray.800" : "text.default"}>
        Desarrollo del Estudio
      </Text>
      <VStack align="stretch" spacing={4}>
        {sections.map((sec, idx) => {
          if (!sec.isNumbered) {
            return (
              <Box
                key={idx}
                bg={isCapture ? "#F7FAFC" : subtleBg}
                border="1px solid"
                borderColor={isCapture ? "#E2E8F0" : borderColor}
                borderRadius="lg"
                p={4}
                style={{ pageBreakInside: 'avoid' }}
              >
                <Text color={isCapture ? "gray.700" : "text.default"} lineHeight="tall">
                  {sec.content}
                </Text>
              </Box>
            );
          }

          const style = getSectionStyle(sec.title);
          const paragraphs = sec.content.split(/\n\s*\n/).filter(Boolean);

          return (
            <Box
              key={idx}
              bg={style.bg}
              border="1px solid"
              borderColor={style.borderColor}
              borderLeft={style.borderLeft}
              borderRadius="lg"
              p={{ base: 4, md: 5 }}
              boxShadow={isCapture ? "none" : "sm"}
              style={{ pageBreakInside: 'avoid' }}
            >
              <HStack spacing={2} mb={3} align="center" flexWrap="wrap">
                <Text fontSize="lg" role="img" aria-label="icono">
                  {style.icon}
                </Text>
                <Badge
                  colorScheme={style.badgeColor}
                  borderRadius="full"
                  px={2.5}
                  py={0.5}
                  fontSize="xs"
                  fontWeight="bold"
                >
                  {sec.num ? `Parte ${sec.num}` : 'Sección'}
                </Badge>
                <Text
                  fontWeight="bold"
                  fontSize="md"
                  color={isCapture ? "gray.800" : "text.default"}
                >
                  {sec.title}
                </Text>
              </HStack>

              <VStack align="start" spacing={3}>
                {paragraphs.map((p, pIdx) => (
                  <Text
                    key={pIdx}
                    color={isCapture ? "gray.700" : "text.default"}
                    fontStyle={style.isPrayer ? "italic" : "normal"}
                    lineHeight="tall"
                    fontSize="sm"
                  >
                    {p.trim()}
                  </Text>
                ))}
              </VStack>
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
}

export default function EstudioBiblico() {
  const isMobile = useBreakpointValue({ base: true, md: false });

  const searchPlaceholder = useBreakpointValue({
    base: 'Buscar…',
    md: 'Buscar por tema, resumen o versículos…',
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isSeriesOpen, onOpen: onSeriesOpen, onClose: onSeriesClose } = useDisclosure();
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [openIndex, setOpenIndex] = useState(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [showFavorites, setShowFavorites] = useState(false);
  const { favorites, toggleFavorite, isFavorite } = useFavorites('iglesia_favoritos_estudios');
  const { toggleCompleted, isCompleted } = useProgress('iglesia_progreso_estudios');
  const summaryBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  
  const contentRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [cachedImage, setCachedImage] = useState(null);
  const currentBg = '#ffffff';

  useEffect(() => {
    let alive = true;
    if (selectedTopic && contentRef.current) {
      setCachedImage(null);
      setTimeout(async () => {
        try {
          const canvas = await html2canvas(contentRef.current, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: currentBg
          });
          const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
          if (alive) setCachedImage(dataUrl);
        } catch (e) {
          console.error('Error pre-generando Estudio', e);
        }
      }, 500); // give the modal 500ms to mount
    }
    return () => { alive = false; };
  }, [selectedTopic]);

  const handleDownloadPDF = () => {
    if (!contentRef.current) return;
    const element = contentRef.current;
    
    // Hide the HTML footer before capturing so we can draw it on every page
    const htmlFooter = element.querySelector('#pdf-html-footer-estudio');
    if (htmlFooter) htmlFooter.style.display = 'none';

    const opt = {
      margin: [15, 10, 25, 10], // top, right, bottom, left in mm
      filename: `${selectedTopic?.tema || 'Estudio'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] }
    };
    
    html2pdf().set(opt).from(element).toPdf().get('pdf').then((pdf) => {
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        
        pdf.setDrawColor(200, 200, 200);
        // Draw line at pageHeight - 18mm
        pdf.line(10, pageHeight - 18, pageWidth - 10, pageHeight - 18);
        
        pdf.setFontSize(10);
        pdf.setTextColor(50, 50, 50);
        pdf.text('SALA EVANGÉLICA BARRIO LEMPIRA', pageWidth / 2, pageHeight - 12, { align: 'center' });
        
        pdf.setFontSize(8);
        pdf.setTextColor(120, 120, 120);
        pdf.text('www.salaevangelica.com', pageWidth / 2, pageHeight - 7, { align: 'center' });
      }
      
      if (htmlFooter) htmlFooter.style.display = 'block';
    }).save();
  };

  const handleDownloadImage = async () => {
    if (!contentRef.current) return;
    
    if (cachedImage) {
      const link = document.createElement('a');
      link.download = `${selectedTopic?.tema || 'Estudio'}.jpg`;
      link.href = cachedImage;
      link.click();
      return;
    }

    setIsGenerating(true);
    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: currentBg
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
      
      setCachedImage(dataUrl);
      
      const link = document.createElement('a');
      link.download = `${selectedTopic?.tema || 'Estudio'}.jpg`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error(err);
    } finally {
      setIsGenerating(false);
    }
  };

  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        // Trae bastante para no paginar desde server (simple)
        const payload = await fetchJson(`${API}/studies?limit=1000`);
        if (!mounted) return;
        const normalized = normalizeStudiesPayload(payload);
        setTopics(normalized);
      } catch (e) {
        if (mounted) setErr(e?.message || 'Error al cargar estudios bíblicos');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (topics && topics.length > 0) {
      const searchId = searchParams.get('searchId');
      if (searchId) {
        const found = topics.find(t => (t.id || t.tema) === searchId);
        if (found) {
          setSelectedTopic(found);
          onOpen();
        }
        setSearchParams({});
      }
    }
  }, [topics, searchParams, setSearchParams, onOpen]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (topics || []).filter((t) => {
      const tema = (t.tema || '').toLowerCase();
      const resumen = (t.resumen || '').toLowerCase();
      const vers = Array.isArray(t.versiculos) ? t.versiculos : [];
      const hitQ =
        !q ||
        tema.includes(q) ||
        resumen.includes(q) ||
        vers.some((v) => (v || '').toLowerCase().includes(q));
      const hitFav = showFavorites ? favorites.includes(t.id || t.tema) : true;
      return hitQ && hitFav;
    });
  }, [query, topics, showFavorites, favorites]);

  const groupedItems = useMemo(() => {
    const groups = {};
    const result = [];
    filtered.forEach(t => {
      if (t.serie) {
        if (!groups[t.serie]) {
          const newGroup = { isSeries: true, serie: t.serie, parts: [] };
          groups[t.serie] = newGroup;
          result.push(newGroup);
        }
        groups[t.serie].parts.push(t);
      } else {
        result.push(t);
      }
    });
    // Sort parts (assuming older ID = earlier part, or sort alphabetically)
    Object.values(groups).forEach(g => {
       g.parts.sort((a, b) => a.id - b.id);
    });
    return result;
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(groupedItems.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = groupedItems.slice(start, start + PAGE_SIZE);

  const handleVerMas = (topic) => {
    if (topic.isSeries) {
      setSelectedSeries(topic);
      onSeriesOpen();
    } else {
      setSelectedTopic(topic);
      onOpen();
    }
  };

  if (loading) {
    return (
      <Box bg="bg.default" flex="1">
        <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
          <HStack justify="center" py={10}><Spinner size="lg" /><Text> Cargando estudios…</Text></HStack>
        </Container>
      </Box>
    );
  }
  if (err) {
    return (
      <Box bg="bg.default" flex="1">
        <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
          <Heading size="md" color="red.600">Error al cargar estudios</Heading>
          <Text color="gray.700" mt={2}>{String(err)}</Text>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg="bg.default" flex="1">
      <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
        <Heading textAlign="center" mb={6} color="text.default">
          Estudio Bíblico
        </Heading>

        <VStack align="stretch" spacing={4} mb={6}>
          <Input
            borderColor="border.default"
            borderWidth="2px"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => { setPage(1); setQuery(e.target.value); }}
            bg="bg.card"
            _placeholder={{ color: 'text.muted' }}
          />

          <FormControl display="flex" alignItems="center">
            <Switch 
              id="favorites-mode-studies" 
              colorScheme="blue" 
              isChecked={showFavorites}
              onChange={(e) => {
                setPage(1);
                setShowFavorites(e.target.checked);
              }}
            />
            <FormLabel htmlFor="favorites-mode-studies" mb="0" ml={3} color="text.default" fontWeight="normal">
              Mostrar solo mis favoritos
            </FormLabel>
          </FormControl>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
          {pageItems.map((topic, idx) => (
            <Box
              key={topic.isSeries ? `serie-${topic.serie}-${idx}` : `${topic.id || topic.tema}-${idx}`}
              bg="bg.card"
              border="1px solid"
              borderColor="border.default"
              borderRadius="xl"
              boxShadow="sm"
              p={6}
              transition="all .25s ease"
              _hover={{ transform: { md: 'translateY(-4px)' }, boxShadow: { md: 'md' } }}
              position="relative"
              display="flex"
              flexDirection="column"
            >
              <Flex w="100%" justify="space-between" align="start" mb={3}>
                <Heading size="md" color="text.default" pr={8}>
                  {topic.isSeries ? `${topic.serie} (${topic.parts.length} partes)` : topic.tema}
                </Heading>
                {!topic.isSeries && (
                  <IconButton
                    icon={isFavorite(topic.id || topic.tema) ? <FaHeart color="#E53E3E" /> : <FiHeart />}
                    variant="ghost"
                    colorScheme="red"
                    size="sm"
                    aria-label="Favorito"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(topic.id || topic.tema);
                    }}
                    position="absolute"
                    top={2}
                    right={2}
                  />
                )}
              </Flex>
              {!topic.isSeries && isCompleted(topic.id || topic.tema) && (
                <Badge colorScheme="green" mb={2} display="flex" alignItems="center" w="fit-content">
                  <CheckCircleIcon mr={1} boxSize={3} /> Completado
                </Badge>
              )}
              <Text color="text.muted" mb={4}>
                {topic.isSeries 
                  ? "Esta serie de estudios está dividida en varias partes. Haz clic para ver el contenido completo de la serie."
                  : topic.resumen}
              </Text>
              <Button 
                colorScheme="blue" 
                variant="solid" 
                size="sm" 
                mt="auto" 
                alignSelf="flex-start"
                onClick={() => handleVerMas(topic)}
              >
                {topic.isSeries ? "Ver Serie" : "Ver más"}
              </Button>
            </Box>
          ))}
        </SimpleGrid>

        {/* Paginación */}
        {groupedItems.length > PAGE_SIZE && (
          <Flex mt={8} justify="center" align="center" gap={4}>
            <Button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              isDisabled={page === 1}
              variant="outline"
              leftIcon={<ArrowLeftIcon color="text.muted" />}
              _hover={{ bg: 'whiteAlpha.200' }}
            />
            <Text color="text.muted">
              Página {page} de {totalPages}
            </Text>
            <Button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              isDisabled={page === totalPages}
              variant="outline"
              rightIcon={<ArrowRightIcon color="text.muted" />}
              _hover={{ bg: 'whiteAlpha.200' }}
            />
          </Flex>
        )}
      </Container>

      {/* Modal para Series */}
      {selectedSeries && (
        <Modal isOpen={isSeriesOpen} onClose={onSeriesClose} size={{ base: 'full', md: 'lg' }} isCentered scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent bg="bg.card" color="text.default">
            <ModalHeader>{selectedSeries.serie}</ModalHeader>
            <ModalBody>
              <VStack align="stretch" spacing={4}>
                <Text color="text.muted" mb={2}>Esta serie contiene {selectedSeries.parts.length} partes.</Text>
                {selectedSeries.parts.map((part, index) => (
                  <Box 
                    key={part.id} 
                    p={4} 
                    borderWidth="1px" 
                    borderColor="border.default" 
                    borderRadius="md"
                    _hover={{ bg: summaryBg, cursor: 'pointer' }}
                    onClick={() => {
                      onSeriesClose();
                      setSelectedTopic(part);
                      onOpen();
                    }}
                  >
                    <Heading size="sm" mb={1}>{part.tema}</Heading>
                    <Text fontSize="sm" color="text.muted" noOfLines={2}>{part.resumen}</Text>
                  </Box>
                ))}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button onClick={onSeriesClose} colorScheme="gray" size="sm">Cerrar</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Modal UNIFICADO (Desktop + Mobile) */}
      {selectedTopic && (
        <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'lg', lg: 'xl' }} isCentered scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent bg="bg.card" color="text.default" maxH="90vh">
            <ModalHeader display="flex" alignItems="center" gap={4} pr={12}>
              <Text flex="1" noOfLines={2}>{selectedTopic.tema}</Text>
              <AudioPlayerButton 
                id={selectedTopic.id || selectedTopic.tema}
                title={selectedTopic.tema} 
                text={[selectedTopic.resumen, selectedTopic.reflexion].filter(Boolean).join(". ")} 
              />
              <IconButton
                icon={isFavorite(selectedTopic.id || selectedTopic.tema) ? <FaHeart color="#E53E3E" /> : <FiHeart />}
                variant="ghost"
                colorScheme="red"
                size="sm"
                aria-label="Favorito"
                onClick={() => toggleFavorite(selectedTopic.id || selectedTopic.tema)}
              />
            </ModalHeader>
            <ModalBody>
              {/* === VERSIÓN VISIBLE EN PANTALLA === */}
              <VStack align="start" spacing={5}>
                {selectedTopic.resumen && (
                  <Box w="100%" bg={summaryBg} p={4} borderRadius="md" border="1px solid" borderColor="border.default">
                    <Text fontWeight="bold" mb={1} color="text.muted" fontSize="sm">Resumen</Text>
                    <Text color="text.default">{selectedTopic.resumen}</Text>
                  </Box>
                )}

                {selectedTopic.versiculos && selectedTopic.versiculos.length > 0 && (
                  <Box w="100%">
                    <Text fontWeight="bold" mb={2}>Versículos sugeridos</Text>
                    <VStack align="start" spacing={1} pl={2} borderLeft="2px solid" borderColor="brand.300">
                      {selectedTopic.versiculos.map((v, i) => (
                        <Text key={i} color="text.muted" fontStyle="italic">• {v}</Text>
                      ))}
                    </VStack>
                  </Box>
                )}

                {(() => {
                  const { watchUrl, embedUrl } = getYouTubeUrls(selectedTopic.video);
                  if (!watchUrl && !embedUrl) return null;
                  return (
                    <Box w="full">
                      <Text fontWeight="bold" mb={2}>Video</Text>
                      {embedUrl && (
                        <AspectRatio ratio={16 / 9} w="full" mb={2} borderRadius="md" overflow="hidden">
                          <iframe
                            src={embedUrl}
                            title={selectedTopic.tema}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                          />
                        </AspectRatio>
                      )}
                      <Link href={watchUrl || '#'} isExternal color="blue.500" fontWeight="medium" rel="noopener noreferrer">
                        Ver en YouTube →
                      </Link>
                    </Box>
                  );
                })()}

                {selectedTopic.reflexion && (
                  <ReflexionViewer text={selectedTopic.reflexion} />
                )}

                <Box w="100%" pt={4}>
                  <Button 
                    colorScheme={isCompleted(selectedTopic.id || selectedTopic.tema) ? "green" : "blue"} 
                    variant={isCompleted(selectedTopic.id || selectedTopic.tema) ? "solid" : "outline"}
                    leftIcon={<CheckCircleIcon />}
                    onClick={() => toggleCompleted(selectedTopic.id || selectedTopic.tema)}
                    w="100%"
                  >
                    {isCompleted(selectedTopic.id || selectedTopic.tema) ? "Leído (Desmarcar)" : "Marcar como leído"}
                  </Button>
                </Box>

                {/* VISIBLE FOOTER */}
                <Box w="100%" pt={8} mt={4} borderTop="1px solid" borderColor="border.default" textAlign="center">
                  <Text fontSize="sm" fontWeight="bold" letterSpacing="widest" color="text.default">
                    SALA EVANGÉLICA BARRIO LEMPIRA
                  </Text>
                  <Text fontSize="xs" color="text.muted" mt={1}>
                    www.salaevangelica.com
                  </Text>
                </Box>
              </VStack>

              {/* === VERSIÓN OCULTA PARA CAPTURA (Siempre Light Mode y con Footer) === */}
              <Box position="absolute" top="-9999px" left="-9999px">
                <LightMode>
                  <Box 
                    ref={contentRef} 
                    w="190mm" 
                    bg="white" 
                    color="gray.800" 
                    display="block"
                    px={8}
                    py={6}
                  >
                    <Box>
                      <VStack align="start" spacing={5}>
                        <Heading size="lg" color="gray.900" mb={2}>{selectedTopic.tema}</Heading>

                        {selectedTopic.resumen && (
                          <Box w="100%" bg="gray.50" p={4} borderRadius="md" border="1px solid" borderColor="gray.200" style={{ pageBreakInside: 'avoid' }}>
                            <Text fontWeight="bold" mb={1} color="gray.600" fontSize="sm">Resumen</Text>
                            <Text color="gray.800">{selectedTopic.resumen}</Text>
                          </Box>
                        )}

                        {selectedTopic.versiculos && selectedTopic.versiculos.length > 0 && (
                          <Box w="100%" style={{ pageBreakInside: 'avoid' }}>
                            <Text fontWeight="bold" mb={2} color="gray.800">Versículos sugeridos</Text>
                            <VStack align="start" spacing={1} pl={2} borderLeft="2px solid" borderColor="brand.300">
                              {selectedTopic.versiculos.map((v, i) => (
                                <Text key={i} color="gray.600" fontStyle="italic">• {v}</Text>
                              ))}
                            </VStack>
                          </Box>
                        )}

                        {selectedTopic.reflexion && (
                          <ReflexionViewer text={selectedTopic.reflexion} isCapture={true} />
                        )}
                      </VStack>
                    </Box>

                    {/* FOOTER HTML (Oculto para PDF, solo se usa en JPG) */}
                    <Box id="pdf-html-footer-estudio" w="100%" pt={8} mt={8} borderTop="1px solid" borderColor="gray.200" textAlign="center">
                      <Text fontSize="sm" fontWeight="bold" letterSpacing="widest" color="gray.800">
                        SALA EVANGÉLICA BARRIO LEMPIRA
                      </Text>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        www.salaevangelica.com
                      </Text>
                    </Box>
                  </Box>
                </LightMode>
              </Box>
            </ModalBody>
            <ModalFooter display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <HStack spacing={2}>
                <Button 
                  colorScheme="blue" 
                  variant="ghost"
                  leftIcon={<FiFileText />} 
                  onClick={handleDownloadPDF}
                  size="sm"
                >
                  PDF
                </Button>
                <Button 
                  colorScheme="green" 
                  variant="ghost"
                  leftIcon={isGenerating ? <Spinner size="xs" /> : <FiImage />} 
                  onClick={handleDownloadImage}
                  isDisabled={isGenerating}
                  size="sm"
                >
                  JPG Full
                </Button>
              </HStack>
              <Button onClick={onClose} colorScheme="gray" size="sm">Cerrar</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}
